import React from 'react';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import {
  MutationRemoveCardFromWorkerArgs,
  Success,
  AppErrorCode,
  useAddCardToWorkerMutation,
} from 'apollo/generated/client-operations';
import { useIsMounted } from 'utils';
import { getGraphQLError, getGraphQLErrorByCode } from 'utils/error';
import { AlertService } from 'components/alertService';
import { CheckIcon } from 'components/icons';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { REMOVE_CARD_FROM_WORKER } from 'containers/worker/helpers/queries';
import { AlreadyExistingBadgeMessage } from 'components/alreadyExistingBadgeMessage';
import { useProximityCardFormatOptions } from 'containers/worker/helpers/useProximityCardFormatOptions';
import { useTempBadgeOptions } from 'containers/worker/helpers/useTempBadgeOptions';
import { ReactivateBadgeState } from 'containers/worker/modals/badgeModal/types';
import { Alert } from '@odin-labs/components';
import { getISODateTimeToFormattedDate } from 'utils/dates';
import {
  WorkerCard,
  ReassignBadgeState,
  WorkerCardAlreadyExistsErrorExtensions,
} from 'containers/worker/modals/badgeModal/components/types';
import {
  getFormDefaultValues,
  getFormInputs,
} from './AssignTempBadgeModalContent.forms';
import { AssignTempBadgeFormData } from './types';

const instructions =
  'Assigning a Temporary Badge overrides onboarding, suspensions, and bans.';

export interface AssignTempBadgeModalContentProps {
  setOpen: () => void;
  onAction: (reassignBadgeState?: ReassignBadgeState) => void;
  jobsiteId: string;
  jobsiteName: string;
  isBannedOnSelectedJobsite: boolean;
  isSuspendedOnSelectedJobsite: boolean;
  suspensionDate?: string | null;
  workerId: string;
  workerCards: WorkerCard[];
  reactivateBadge: (reactivateBadgeState: ReactivateBadgeState) => void;
}

export function AssignTempBadgeModalContent(
  props: AssignTempBadgeModalContentProps,
): React.ReactElement {
  const {
    setOpen,
    onAction,
    reactivateBadge,
    jobsiteId,
    jobsiteName,
    workerId,
    workerCards,
    isBannedOnSelectedJobsite,
    isSuspendedOnSelectedJobsite,
    suspensionDate,
  } = props;

  const isMounted = useIsMounted();
  const [isFetching, setIsFetching] = React.useState<boolean>(false);

  const [addCardToWorker] = useAddCardToWorkerMutation();

  const [removeCardFromWorker] = useMutation<
    Success,
    MutationRemoveCardFromWorkerArgs
  >(REMOVE_CARD_FROM_WORKER, {
    onError: (error) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
      setIsFetching(false);
    },
  });

  const onSubmit: FormOnSubmit<AssignTempBadgeFormData> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isFetching) return;
    setIsFetching(true);

    const { tempBadgeId, expirationDate, tempBadgeReason, badges } = data;

    const addCardToWorkerAndRemoveExisting = async (options?: {
      forceCreate?: boolean;
    }): Promise<void> => {
      await addCardToWorker({
        variables: {
          jobsiteId,
          workerId,
          forceCreate: options?.forceCreate,
          workerCardInput: {
            tempBadgeId: tempBadgeId?.value,
            isTemporary: true,
            startDate: moment().toDate(),
            endDate: expirationDate.endOf('day').toDate(),
            cardIssueReason: tempBadgeReason,
          },
        },
      });

      const workerCardIdsToDeactivate = badges
        .filter(({ selected }) => selected)
        .map(({ workerCard }) => workerCard.workerCardId);

      if (workerCardIdsToDeactivate.length) {
        await removeCardFromWorker({
          variables: {
            workerCardIds: workerCardIdsToDeactivate,
          },
        });
      }
    };

    try {
      await addCardToWorkerAndRemoveExisting();
      AlertService.alert('success', 'Success', 'Badge assigned');
      onAction();
    } catch (error) {
      event.preventDefault();

      const workerCardAlreadyExistsError =
        getGraphQLErrorByCode<WorkerCardAlreadyExistsErrorExtensions>(
          error,
          AppErrorCode.WorkerCardAlreadyExists,
        );
      if (workerCardAlreadyExistsError) {
        const { scope, workerCardInfo, canBeReactivated } =
          workerCardAlreadyExistsError.extensions ?? {};

        if (scope === 'onDifferentWorker') {
          onAction({
            workerCardInfo,
            reassignBadge: async (): Promise<void> => {
              await addCardToWorkerAndRemoveExisting({ forceCreate: true });
            },
          });
        } else {
          const { workerCardId } = workerCardInfo?.[0] ?? {};
          formApi.setError('tempBadgeId', {
            message: (
              <AlreadyExistingBadgeMessage
                canBeReactivated={canBeReactivated}
                errorMessage={workerCardAlreadyExistsError.message}
                onClick={(): void =>
                  reactivateBadge({
                    workerCardToReactivate: { id: workerCardId },
                    expirationDate,
                  })
                }
              />
            ),
            shouldFocus: true,
          });
        }
      } else {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
      }
    } finally {
      if (isMounted()) {
        setIsFetching(false);
      }
    }
  };

  const tempBadgeOptions = useTempBadgeOptions(jobsiteId);

  const { proximityCardFormatOptions, error: getJobsiteCardFormatsError } =
    useProximityCardFormatOptions(jobsiteId);

  const formInputs = React.useMemo(
    () =>
      getFormInputs({
        jobsiteId,
        tempBadgeOptions,
        proximityCardFormatOptions,
      }),
    [jobsiteId, tempBadgeOptions, proximityCardFormatOptions],
  );
  const defaultValues = React.useMemo(
    () =>
      getFormDefaultValues({
        workerCards,
        proximityCardFormatOptions,
      }),
    [
      // remove workerCards from dependency so that `defaultValues` won't get recalculated
      // when `workerCards` are re-fetched after adding/removing cards
      proximityCardFormatOptions,
    ],
  );

  const badgeInfo = (
    <div className="odin-mb-6">
      <p className="odin-mb-2">{instructions}</p>
      {isBannedOnSelectedJobsite && (
        <Alert
          type="danger"
          text={`This worker is permanently banned from ${jobsiteName}. A temporary badge will grant immediate access.`}
        />
      )}
      {isSuspendedOnSelectedJobsite && (
        <Alert
          type="warn"
          text={`This worker is suspended from ${jobsiteName} until ${getISODateTimeToFormattedDate(
            suspensionDate,
          )}. A temporary badge will grant immediate access.`}
        />
      )}
    </div>
  );

  return (
    <ModalFormContent
      setOpen={setOpen}
      renderAbove={badgeInfo}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Assign badge"
      actionIcon={CheckIcon}
      actionButtonWithSpinner={isFetching}
      error={getJobsiteCardFormatsError}
    />
  );
}
